import { HttpErrorResponse } from '@angular/common/http';
import {
  afterNextRender,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { ETAPA_TERMO, Requerimento, Termo } from '../../../core/models/requerimento';
import { PdfDocument } from '../../../shared/components/pdf-document/pdf-document';
import { Steps } from '../../../shared/components/steps/steps';
import { LoaderService } from '../../../shared/services/loader-service';
import { RequerimentoService } from '../../../shared/services/requerimento-service';
import { RequerimentoState } from '../../../shared/services/requerimento-state';
import { ToastService } from '../../../shared/services/toast-service';
import {
  distribuirErrosDaApi,
  mensagemComDetalhe,
  precisaRevisarBase,
  ROTA_PAGINA_INDISPONIVEL,
  rotaDoErroRequerimento,
} from '../../../shared/utils/requerimento-erros';
import { centralizarModalNaAreaVisivel, VivestModalElement } from '../../../shared/utils/modal-iframe';

const MENSAGEM_ERRO_ASSINAR = 'Não foi possível assinar o termo. Tente novamente.';
const MENSAGEM_ERRO_ENVIAR = 'Seu termo foi assinado, mas não foi possível enviar a solicitação. Tente novamente.';

/** A mensagem junta o texto fixo e o motivo da API: fica mais tempo na tela que o padrão de 3 s. */
const DURACAO_TOAST_ERRO_MS = 8000;

@Component({
  imports: [PdfDocument, Steps],
  selector: 'app-assinatura-termo-requerimento',
  styleUrl: './assinatura-termo-requerimento.scss',
  templateUrl: './assinatura-termo-requerimento.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AssinaturaTermoRequerimento implements OnInit {
  private readonly router = inject(Router);
  private readonly loaderService = inject(LoaderService);
  private readonly requerimentoService = inject(RequerimentoService);
  private readonly requerimentoState = inject(RequerimentoState);
  private readonly toastService = inject(ToastService);
  private readonly injector = inject(Injector);

  private readonly modalSucesso = viewChild<ElementRef<VivestModalElement>>('modalSucesso');

  readonly stepAtual = signal(7);
  readonly totalSteps = signal(7);
  /** Endereço local (blob:) do PDF do termo; enquanto vazio, o visualizador não é renderizado. */
  readonly pdfSrc = signal<string | undefined>(undefined);
  /** O botão "Assinar termo" só é habilitado depois de marcar "Li e estou de acordo". */
  readonly aceiteTermo = signal(false);
  /** Termo exibido: o hash dele volta no aceite. Já aceito (envio que falhou antes), falta só o envio. */
  readonly termo = signal<Termo | null>(null);
  /** Impede um segundo clique enquanto o aceite (até cerca de 1 minuto) e o envio estão em andamento. */
  readonly assinando = signal(false);
  /** Modal "Solicitação enviada com sucesso!", aberto quando o envio dá certo; o botão leva aos detalhes. */
  readonly openModalSucesso = signal(false);

  constructor() {
    // Libera da memória o PDF criado com URL.createObjectURL ao sair da tela.
    inject(DestroyRef).onDestroy(() => this.liberarPdf());

    // O modal aparece no meio da área que a pessoa está vendo, e não no meio da página inteira.
    effect((onCleanup) => {
      if (!this.openModalSucesso()) return;

      let parar = (): void => {};
      // Depois da renderização, quando o vivest-modal já recebeu o `open`.
      const render = afterNextRender(
        () => {
          const elemento = this.modalSucesso()?.nativeElement;
          if (elemento) parar = centralizarModalNaAreaVisivel(elemento);
        },
        { injector: this.injector },
      );

      onCleanup(() => {
        render.destroy();
        parar();
      });
    });
  }

  async ngOnInit(): Promise<void> {
    this.loaderService.showLoader('Carregando...');
    try {
      const requerimento = await this.requerimentoState.carregar();
      if (requerimento.status !== 'R') {
        // Já enviado: o termo não é aceito de novo.
        this.router.navigate(['/requerimento-beneficio/detalhes']);
        return;
      }
      await this.carregarTermo(requerimento.id);
    } catch (erro) {
      this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /** O vivest-checkbox dispara `change` e expõe o estado na propriedade `checked`. */
  protected onAceiteChange(event: Event): void {
    this.aceiteTermo.set((event.target as HTMLElement & { checked: boolean }).checked);
  }

  onReturn(): void {
    this.router.navigate(['/requerimento-beneficio/resumo']);
  }

  /** "Ver detalhes" do modal de sucesso. A solicitação já foi enviada: fechar o modal também leva aos detalhes. */
  verDetalhes(): void {
    this.openModalSucesso.set(false);
    this.router.navigate(['/requerimento-beneficio/detalhes']);
  }

  async onSubmit(): Promise<void> {
    const termo = this.termo();
    if (!this.aceiteTermo() || !termo || this.assinando()) return;

    const requerimento = this.requerimentoState.requerimento();
    if (!requerimento) {
      this.router.navigate(['/requerimento-beneficio']);
      return;
    }

    this.assinando.set(true);
    try {
      if (termo.aceito || (await this.aceitar(requerimento, termo))) {
        await this.enviar(requerimento.id);
      }
    } finally {
      this.assinando.set(false);
    }
  }

  private async carregarTermo(id: number): Promise<void> {
    const [termo, pdf] = await Promise.all([
      this.requerimentoService.getTermo(id),
      this.requerimentoService.getArquivoTermo(id),
    ]);
    this.termo.set(termo);
    this.aceiteTermo.set(termo.aceito);
    this.liberarPdf();
    this.pdfSrc.set(URL.createObjectURL(pdf));
  }

  private async aceitar(requerimento: Requerimento, termo: Termo): Promise<boolean> {
    this.loaderService.showLoader('Assinando o termo. Isso pode levar até 1 minuto...');
    try {
      this.termo.set(await this.requerimentoService.aceitarTermo(requerimento.id, termo.hashTermo));
      // A API leva a jornada para a etapa do termo no aceite; o resumo usa isso para não refazer o PUT,
      // que invalidaria o aceite.
      this.requerimentoState.definir({
        ...requerimento,
        etapaAtual: Math.max(requerimento.etapaAtual, ETAPA_TERMO),
      });
      return true;
    } catch (erro) {
      await this.tratarErro(erro, requerimento.id, MENSAGEM_ERRO_ASSINAR);
      return false;
    } finally {
      this.loaderService.hideLoader();
    }
  }

  private async enviar(id: number): Promise<void> {
    this.loaderService.showLoader('Enviando a solicitação...');
    try {
      this.requerimentoState.definir(await this.requerimentoService.enviar(id));
      this.openModalSucesso.set(true);
    } catch (erro) {
      await this.tratarErro(erro, id, MENSAGEM_ERRO_ENVIAR);
    } finally {
      this.loaderService.hideLoader();
    }
  }

  /**
   * 409 (termo mudou, já aceito, aceite invalidado) e 503 (GED ou CRM indisponível) ficam na tela, com o texto fixo e o
   * motivo da API; o 409 recarrega o termo para o participante ler a versão atual. O resto segue o padrão das etapas.
   */
  private async tratarErro(erro: unknown, id: number, mensagemPadrao: string): Promise<void> {
    if (erro instanceof HttpErrorResponse && (erro.status === 409 || erro.status === 503)) {
      this.toastService.show(mensagemComDetalhe(erro, mensagemPadrao), 'error', DURACAO_TOAST_ERRO_MS);
      // A base unificada mudou depois da leitura do envio: o participante revisa o cadastro antes do termo.
      if (precisaRevisarBase(erro)) {
        await this.router.navigate(['/requerimento-beneficio/dados-cadastrais'], { state: { revisarBase: true } });
        return;
      }
      if (erro.status === 409) await this.recarregarTermo(id);
      return;
    }

    const rota = rotaDoErroRequerimento(erro);
    if (rota) {
      this.router.navigate([rota]);
      return;
    }

    distribuirErrosDaApi(erro, {}, mensagemPadrao).forEach((mensagem) =>
      this.toastService.show(mensagem, 'error'),
    );
  }

  private async recarregarTermo(id: number): Promise<void> {
    try {
      await this.carregarTermo(id);
    } catch (erro) {
      this.router.navigate([rotaDoErroRequerimento(erro) ?? ROTA_PAGINA_INDISPONIVEL]);
    }
  }

  private liberarPdf(): void {
    const src = this.pdfSrc();
    if (src) URL.revokeObjectURL(src);
  }
}
